import axios from 'axios';
import auth from './auth';

/**
 * @typedef {Object} RequestOptions
 * @property {boolean} [authHeader=true] - Send Authorization header with token in request
 * @property {Object} [headers={}] - Other headers to include in the request
 * @property {boolean} [rawRequest=false] - Return the original response object instead of just the payload
 */

const getAuthHeaders = () => {
    const bearerToken = auth.getBearerToken();
    let headers = {};
    if (bearerToken)
        headers.Authorization = `Bearer ${bearerToken}`;
    return headers;
}

/**
 * Make an HTTP GET request with optional substitution
 * @param {string} endpoint - URL to send a GET request to
 * @param {Object} [params] - Key:Value pairs to substitute in the URL
 * @param {RequestOptions} [options] - Request options
 */
export const get = (
    endpoint,
    params = {},
    { 
        authHeader = true, 
        headers = {
            'x-app-id': process.env.REACT_APP_APPID,
        }, 
        rawRequest = false, 
        ...options 
    } = {}
) => {
   
    const query = Object.keys(params).reduce((previousValue, currentValue)=>{
        //handles sending arrays of values
        if(Array.isArray(params[currentValue])){
            return previousValue += params[currentValue].map((arrayValue,index)=> {
                return `${currentValue}[${index}]=${arrayValue}`
            }).join('&')
        }
        //handles normal way of sending params
        return previousValue += `${currentValue}=${params[currentValue]}&`;
    }, '?');
    let target = endpoint;
    if (query !== '?')
        target += query;
    let config = { headers, ...options };

    if (authHeader) {
        config.headers = {
            ...config.headers,
            ...getAuthHeaders()
        };
    }

    return axios.get(target, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => {
            const { data, status } = ex.response;
            
            if(status == 401) {
                window.location = '/unauthorized';
            }

            return {
                ...data
            };
        });
};

/**
 * Make an HTTP POST request
 * @param {string} endpoint - URL to send a POST request to
 * @param {Object} [data] - Body of the request
 * @param {RequestOptions} [options] - Request options
 */
export const post = (
    endpoint,
    data = {},
    { 
        authHeader = true, 
        headers = {
            'x-app-id': process.env.REACT_APP_APPID,
        }, 
        rawRequest = false, 
        ...options 
    } = {}
) => {
    let config = { headers, ...options };

    if (authHeader) {
        config.headers = {
            ...config.headers,
            ...getAuthHeaders()
        };
    }
   
    return axios.post(endpoint, data, config)
        .then(response => {
            return rawRequest ? response : response.data;
        })
        .catch(ex => ({ ...ex.response.data }));
};

/**
 * Make an HTTP DELETE request with optional substitution
 * @param {string} endpoint - URL to send a DELETE request to
 * @param {Object} [params] - Key:Value pairs to substitute in the URL
 * @param {RequestOptions} [options] - Request options
 */
export const remove = (
    endpoint,
    params = {},
    { 
        authHeader = true, 
        headers = {
            'x-app-id': process.env.REACT_APP_APPID,
        }, 
        rawRequest = false, 
        ...options 
    } = {}
) => {

    const query = Object.keys(params).reduce((previousValue, currentValue)=>{
        //handles sending arrays of values
        if(Array.isArray(params[currentValue])){
            return previousValue += params[currentValue].map((arrayValue,index)=> {
                return `${currentValue}[${index}]=${arrayValue}`
            }).join('&')
        }
        //handles normal way of sending params
        return previousValue += `${currentValue}=${params[currentValue]}&`;
    }, '?');

    let target = endpoint;
    if (query !== '?')
        target += query;
    let config = { headers, ...options };

    if (authHeader) {
        config.headers = {
            ...config.headers,
            ...getAuthHeaders()
        };
    }

    console.log(target, ' target in eclipse ui')

    return axios.delete(target, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => ({ errored: true, error: (ex.response ? ex.response.data.error : ex.message) || ex }));
};

/**
 * Make an HTTP PUT request
 * @param {string} endpoint - URL to send a PUT request to
 * @param {Object} [data] - Body of the request
 * @param {RequestOptions} [options] - Request options
 */
export const put = (
    endpoint,
    data = {},
    { 
        authHeader = true, 
        headers = {
            'x-app-id': process.env.REACT_APP_APPID,
        }, 
        rawRequest = false, 
        ...options 
    } = {}
) => {

    let config = { headers, ...options };

    if (authHeader) {
        config.headers = {
            ...config.headers,
            ...getAuthHeaders()
        };
    }
    
    return axios.put(endpoint, data, config)
        .then(response => {
            return rawRequest ? response : response.data;
        })
        .catch(ex => { 
            return {
                ...ex.response.data
            };
        });
};

/**
 * Make an HTTP PATCH request
 * @param {string} endpoint - URL to send a PATCH request to
 * @param {Object} [data] - Body of the request
 * @param {RequestOptions} [options] - Request options
 */
export const patch = (
    endpoint,
    data = {},
    { 
        authHeader = true, 
        headers = {
            'x-app-id': process.env.REACT_APP_APPID,
        }, 
        rawRequest = false, 
        ...options 
    } = {}
) => {

    let config = { headers, ...options };

    if (authHeader) {
        config.headers = {
            ...config.headers,
            ...getAuthHeaders()
        };
    }
    
    return axios.patch(endpoint, data, config)
        .then(response => {
            return rawRequest ? response : response.data;
        })
        .catch(ex => { 
            return {
                ...ex.response.data
            };
        });
};

export default {
    get: (params, endpoint, options) => get(endpoint, params, options),
    post: (data, endpoint, options) => post(endpoint, data, options),
    delete: (params, endpoint) => remove(endpoint, params),
    put: (data, endpoint, options) => put(endpoint, data, options),
    patch: (data, endpoint, options) => patch(endpoint, data, options),
    putNoAuth: (data, endpoint, options) => put(endpoint, data, {...options, authHeader: false})
};
